// Pending + staged-reveal UI for /result.
//
// Two distinct visual modes:
//
//   1. WAIT MODE (revealStage === 0): the long ~25-30s phase while
//      fig-verify is mid-flight. Visuals are deliberately CALM:
//      photos at top + one quiet "examining" panel with cycling status.
//      No scan-line, no shimmer skeletons, no competing motion.
//
//   2. REVEAL MODE (revealStage >= 1): the ~2.5s phase after the result
//      blob arrives. The quiet panel collapses and four content blocks
//      fade in sequentially (verdict → summary → signals → pricing),
//      each with a fig-reveal animation so the cascade is perceptible.
//
// At revealStage 4 the parent (Result.tsx) unmounts this component and
// swaps to the full Result render.

import { useState, useEffect } from "react";
import type { FigVerifyResult } from "@/lib/scanStore";

interface Props {
  photos: string[];
  /** 0 = WAIT mode (calm). 1-4 = REVEAL mode (cascade). See file header. */
  revealStage?: number;
  /** Verify result blob — populated once fig-verify writes back. */
  result?: FigVerifyResult | null;
  /** Optional override for the cycling status stages. */
  stages?: string[];
}

const DEFAULT_STAGES = [
  "Reading the threads…",
  "Checking the reference doc…",
  "Comparing to the catalog…",
  "Calibrating pricing…",
];
const STAGE_INTERVAL_MS = 6_000;

export function ResultPending({
  photos,
  revealStage = 0,
  result = null,
  stages = DEFAULT_STAGES,
}: Props) {
  const primaryPhoto = photos[0];
  const inWait = revealStage === 0;
  const verdictReady = revealStage >= 1 && result != null;
  const summaryReady = revealStage >= 2 && result != null;
  const signalsReady = revealStage >= 3 && result != null;
  const pricingReady = revealStage >= 4 && result != null;

  // Cycling status text — only ticks during WAIT mode.
  const [statusIdx, setStatusIdx] = useState(0);
  useEffect(() => {
    if (!inWait || stages.length <= 1) return;
    const t = setInterval(
      () => setStatusIdx((i) => Math.min(i + 1, stages.length - 1)),
      STAGE_INTERVAL_MS,
    );
    return () => clearInterval(t);
  }, [inWait, stages.length]);
  const currentStatus = stages[statusIdx] ?? stages[stages.length - 1];

  return (
    <div style={{ padding: "16px 16px 40px" }}>
      {/* Photos — visible the entire time. Calm during WAIT (no scan-line
          overlay); same during REVEAL. */}
      {primaryPhoto && (
        <div style={{
          position: "relative",
          width: "100%",
          aspectRatio: "4/3",
          border: "1px solid hsl(var(--ink))",
          overflow: "hidden",
          background: "hsl(var(--paper-3))",
          marginBottom: 14,
        }}>
          <img
            src={primaryPhoto}
            alt="Submitted scan"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
      )}

      {/* Photo strip for additional shots — kept simple, no animations. */}
      {photos.length > 1 && (
        <div style={{
          display: "flex", gap: 6, marginBottom: 14,
          overflowX: "auto",
        }}>
          {photos.slice(1).map((p, i) => (
            <img
              key={i}
              src={p}
              alt=""
              style={{
                width: 60, height: 60, objectFit: "cover", flexShrink: 0,
                border: "1px solid hsl(var(--ink))",
              }}
            />
          ))}
        </div>
      )}

      {inWait ? (
        // ── WAIT MODE — single calm panel ────────────────────────────────
        // Replaces the previous four-skeleton stack + scan-line + cycling
        // status combo. One source of motion (pulsing dot in the status
        // line), nothing else competes for the eye.
        <div style={{
          marginTop: 8,
          padding: "32px 20px",
          background: "hsl(var(--cream))",
          border: "1px solid hsl(var(--ink))",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 14,
          textAlign: "center",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "hsl(var(--rust))",
          }}>
            <div
              aria-hidden
              style={{
                width: 8, height: 8, borderRadius: "50%",
                background: "hsl(var(--rust))",
                animation: "fig-pulse 1.2s infinite",
              }}
            />
            <span>FIG is examining</span>
          </div>
          <div
            key={currentStatus}
            style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 22, fontWeight: 700,
              color: "hsl(var(--ink))",
              lineHeight: 1.2,
              animation: "fig-fadeup 0.5s",
              maxWidth: 320,
            }}
          >
            {currentStatus}
          </div>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 13,
            color: "hsl(var(--ink-2))",
            opacity: 0.8,
            maxWidth: 320,
          }}>
            Verifications take about 30 seconds.
          </div>
        </div>
      ) : (
        // ── REVEAL MODE — cascade four sections in order ─────────────────
        <>
          {verdictReady && (
            <div className="fig-reveal">
              <VerdictMini result={result!} />
            </div>
          )}
          {summaryReady && (
            <div className="fig-reveal">
              <SummaryMini result={result!} />
            </div>
          )}
          {signalsReady && (
            <div className="fig-reveal">
              <SignalsMini result={result!} />
            </div>
          )}
          {pricingReady && (
            <div className="fig-reveal">
              <PricingMini result={result!} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Real-content blocks for REVEAL mode ──────────────────────────────────
// Only mount once their stage threshold is reached. The parent Result.tsx
// fully unmounts this component at revealStage 4 + cascadingResult clear,
// at which point the full Result.tsx render takes over with proper
// expandable Sections / forensic detail / etc.

function VerdictMini({ result }: { result: FigVerifyResult }) {
  const r = result as unknown as Record<string, unknown>;
  const verdict = String(
    r.internal_verdict
    ?? (r.assessment as Record<string, unknown> | undefined)?.verdict
    ?? "Verified",
  );
  const verdictPretty = verdict
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <div style={{
      display: "flex", justifyContent: "center", padding: "20px 0 12px",
    }}>
      <div style={{
        padding: "12px 28px",
        borderRadius: 999,
        background: "hsl(var(--ink))",
        color: "hsl(var(--ink-fg))",
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontWeight: 800,
        fontSize: 18,
        letterSpacing: "-0.01em",
        minWidth: 220,
        textAlign: "center",
      }}>
        {verdictPretty}
      </div>
    </div>
  );
}

function SummaryMini({ result }: { result: FigVerifyResult }) {
  const r = result as unknown as Record<string, unknown>;
  const item = (r.item ?? {}) as Record<string, unknown>;
  const meta = (r.meta ?? {}) as Record<string, unknown>;
  const rows: Array<[string, string]> = [
    ["Era", String(item.estimated_era ?? "—")],
    ["Brand", String(item.brand_detected ?? "—")],
    ["Category", String(meta.item_category ?? item.detected_type ?? "—")],
    ["Material", String(item.material_description ?? "—")],
  ];
  return (
    <div style={{
      padding: "14px 16px",
      background: "hsl(var(--paper-2))",
      border: "1px solid hsl(var(--ink))",
      marginBottom: 12,
    }}>
      {rows.map(([label, value], i) => (
        <div key={label} style={{ marginBottom: i === rows.length - 1 ? 0 : 12 }}>
          <div className="caps" style={{ color: "hsl(var(--ink-2))", marginBottom: 4, fontSize: 9 }}>
            {label}
          </div>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 14, color: "hsl(var(--ink))",
          }}>
            {value || "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

function SignalsMini({ result }: { result: FigVerifyResult }) {
  const r = result as unknown as Record<string, unknown>;
  const signals = (r.signals_observed ?? []) as Array<Record<string, unknown>>;
  const obs = (r.observations ?? {}) as Record<string, unknown>;
  const eraEvidence = (obs.era_evidence ?? []) as unknown[];
  const items: string[] = signals.length > 0
    ? signals.slice(0, 3).map((s) => String(s.feature ?? ""))
    : eraEvidence.slice(0, 3).map((s) => String(s));
  return (
    <div style={{
      padding: "14px 16px",
      background: "hsl(var(--paper))",
      border: "1px solid hsl(var(--ink))",
      marginBottom: 12,
    }}>
      <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 10 }}>
        Signals Observed
      </div>
      {items.length === 0 ? (
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontSize: 13, color: "hsl(var(--ink-2))",
        }}>
          No notable signals.
        </div>
      ) : items.map((text, i) => (
        <div key={i} style={{
          display: "flex", gap: 10,
          marginBottom: i === items.length - 1 ? 0 : 10,
        }}>
          <div
            aria-hidden
            style={{
              width: 5, height: 5, borderRadius: "50%",
              background: "hsl(var(--rust))",
              flexShrink: 0, marginTop: 8,
            }}
          />
          <div style={{
            flex: 1,
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 13, color: "hsl(var(--ink))", lineHeight: 1.4,
          }}>
            {text}
          </div>
        </div>
      ))}
    </div>
  );
}

function PricingMini({ result }: { result: FigVerifyResult }) {
  const r = result as unknown as Record<string, unknown>;
  const pricing = (r.pricing ?? {}) as Record<string, unknown>;
  const guidance = (pricing.guidance ?? null) as Record<string, unknown> | null;
  const compCount = (pricing.comparable_count ?? 0) as number;

  if (!guidance) {
    return (
      <div style={{
        padding: "14px 16px",
        background: "hsl(var(--cream))",
        border: "1px solid hsl(var(--ink))",
      }}>
        <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>
          Estimated Value
        </div>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontSize: 14, color: "hsl(var(--ink-2))", lineHeight: 1.4,
        }}>
          Insufficient market data — verify with current listings before pricing.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: "14px 16px",
      background: "hsl(var(--cream))",
      border: "1px solid hsl(var(--ink))",
    }}>
      <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
        Estimated Value
      </div>
      <div style={{
        fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800,
        fontSize: 30, color: "hsl(var(--ink))", lineHeight: 1.05,
        letterSpacing: "-0.02em",
      }}>
        {String(guidance.recommended_range ?? "—")}
      </div>
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
        color: "hsl(var(--ink-2))", marginTop: 10, letterSpacing: "0.06em",
      }}>
        Based on {compCount} comparable listing{compCount !== 1 ? "s" : ""}
      </div>
    </div>
  );
}
